import { useEffect } from 'react';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import type { EditAiStripModalProps } from '.';

/**
 * Coordinates the two panes of the AI strip edit dialog, the basic form and the extended more-info editor. Tracks which pane is showing plus the payload handed between them, resets both whenever the modal is hidden, and emits the open-modal event on every visibility change.
 *
 * @param props - Modal props; only visible is read, to reset the panes and broadcast the open state.
 * @returns An object carrying state, handleEnterEditMoreInfo, handleCancelEditMoreInfo and handleSaveEditMoreInfo.
 */
const useEditAiStripModal = ({ visible }: Pick<EditAiStripModalProps, 'visible'>) => {
  const [state, setState] = useMergeState<any>({
    isEditMoreInfo: false,
    transferData: undefined,
  });

  /**
   * Switches the strip dialog into the extended more-info editor. Raises the more-info flag and stores the payload handed over from the basic form.
   *
   * @param data - Current strip form values passed through to the more-info editor.
   * @returns Nothing.
   */
  const handleEnterEditMoreInfo = (data: any) => {
    setState({ isEditMoreInfo: true, transferData: data });
  };

  /**
   * Drops out of the extended strip editor without carrying anything back. Clears the more-info flag and discards the transfer payload.
   *
   * @returns Nothing.
   */
  const handleCancelEditMoreInfo = () => {
    setState({ isEditMoreInfo: false, transferData: undefined });
  };

  /**
   * Returns from the extended strip editor keeping its edits. Lowers the more-info flag and retains the payload so the basic form picks up the new values.
   *
   * @param data - Edited strip values handed back from the more-info editor.
   * @returns Nothing.
   */
  const handleSaveEditMoreInfo = (data: any) => {
    setState({ isEditMoreInfo: false, transferData: data });
  };

  useEffect(() => {
    if (!visible) {
      setState({ isEditMoreInfo: false, transferData: undefined });
    }
    emitter.emit(EMITTER_CONSTANTS.OPEN_MODAL_EDIT_AI_STRIP, { value: visible });
  }, [visible]);

  return {
    state,
    handleEnterEditMoreInfo,
    handleCancelEditMoreInfo,
    handleSaveEditMoreInfo,
  };
};

export default useEditAiStripModal;
